// CSS Custom Highlight API를 이름별 단일 인스턴스로 공유한다.
//
// 같은 document 안에 TextFinderProvider가 여러 개(메인 리스트/linked 리스트 등)
// mount될 수 있는데, 각 provider가 매번 new Highlight()로 레지스트리 키를 덮어쓰면
// 마지막 provider만 살아남아(last-writer-wins) 나머지 하이라이트가 사라진다.
// 이름당 Highlight 인스턴스를 1회만 만들어 CSS.highlights에 등록하고, 각 provider는
// 자신의 Range만 add/delete 하도록 해서 충돌을 원천 차단한다.

interface HighlightType {
  add(range: Range): void
  delete(range: Range): boolean
  clear(): void
}

interface HighlightConstructor {
  new (): HighlightType
}

interface HighlightRegistry {
  set(name: string, highlight: HighlightType): void
  get(name: string): HighlightType | undefined
  clear(): void
  delete(name: string): boolean
}

declare global {
  interface Window {
    Highlight: HighlightConstructor
  }
}

// Type augmentation for CSS global
declare const CSS: typeof globalThis.CSS & {
  highlights: HighlightRegistry
}

const instances = new Map<string, HighlightType>()

// 이름당 Highlight 인스턴스를 lazy하게 생성/등록해서 반환한다.
// Highlight API 미지원 환경(jsdom 등)에서는 null을 반환해 호출부가 no-op 처리한다.
export const getSharedHighlight = (name: string): HighlightType | null => {
  if (typeof window === 'undefined' || window.Highlight == null) {
    return null
  }

  const existing = instances.get(name)
  if (existing != null) {
    return existing
  }

  const highlight = new window.Highlight()
  CSS.highlights.set(name, highlight)
  instances.set(name, highlight)
  return highlight
}
